import type { SortDirectionType } from '@rio-cloud/rio-uikit/SortDirection';
import type { ColumnDetailsMap } from '@rio-cloud/rio-uikit/TableCol';
import cloneDeep from 'lodash/fp/cloneDeep';
import compact from 'lodash/fp/compact';
import get from 'lodash/fp/get';
import isEmpty from 'lodash/fp/isEmpty';
import { createSelector } from 'reselect';

import type { RootState } from '../../configuration/setup/store';
import { type CombinedAssetData, NotificationStates, type TransformedData } from '../../services/types';
import { getData, getSelectedAssetGroupIds, getTransformedData } from '../app/selectors';
import { TABLE_COLUMN_ACTIVITY, tableConfig } from './tableConfig';
import { isMatchingValue, sortItems } from './tableUtils';

export const getSearchValue = get('app.table.search');
export const getSortBy = get('app.table.sortBy');
export const getSortDir = get('app.table.sortDir');

export const getColumns = get('app.table.columns');
export const getColumnOrder = get('app.table.columns.columnOrder');
export const getHiddenColumns = get('app.table.columns.hiddenColumns');
export const getColumnsDetails = (state: RootState): ColumnDetailsMap =>
    cloneDeep(state.app.table.columns.columnsDetails);

export const getVisibleColumns = createSelector(getHiddenColumns, getColumnOrder, (hidden = [], order = []) =>
    order.filter((name: string) => !hidden.includes(name))
);

export const showTableSettingsDialog = get('app.table.showTableSettings');
export const getChunkCounter = get('app.table.chunks');
export const getViewType = get('app.table.viewType');
export const getActivityFilter = get('app.table.activityFilter');
export const getAssetFilter = get('app.table.assetFilter');
export const getNotificationFilter = get('app.table.notificationFilter');

export const searchItems = (
    items: TransformedData[],
    columns: (keyof TransformedData)[],
    searchValue?: string
): TransformedData[] => {
    if (!searchValue) {
        return items;
    }

    return items.filter(item =>
        columns.some(col => {
            const value = item[col] ?? '';
            if (col === TABLE_COLUMN_ACTIVITY) {
                return isMatchingValue(item.activityDuration, searchValue);
            }
            return isMatchingValue(value as string, searchValue);
        })
    );
};

export const filterByActivity = (items: TransformedData[], activityFilter: string[]): TransformedData[] => {
    if (isEmpty(activityFilter)) {
        return items;
    }

    return items.filter(item => {
        const activity = item[TABLE_COLUMN_ACTIVITY]?.toLowerCase();
        return activity && activityFilter.includes(activity);
    });
};

export const filterByGroups = (items: TransformedData[], selectedGroupIds: string[]): TransformedData[] => {
    if (isEmpty(selectedGroupIds)) {
        return items;
    }

    const selectedGroupSet = new Set(selectedGroupIds);
    return items.filter(item => item.groupIds.some(groupId => selectedGroupSet.has(groupId)));
};

export const filterByAssetTypesOrDriver = (items: TransformedData[], assetFilter: string[]): TransformedData[] => {
    if (isEmpty(assetFilter)) {
        return items;
    }

    const filterDriver = assetFilter.includes('driver') ? (driver?: any) => !!driver : () => false;

    return items.filter(({ type, driver }) => assetFilter.includes(type) || filterDriver(driver));
};

export const filterByNotifications = (items: TransformedData[], notificationFilter: string[]): TransformedData[] => {
    if (isEmpty(notificationFilter)) {
        return items;
    }

    return items.filter(item => {
        const hasExceptions = (item.notificationExceptions ?? 0) > 0;
        const hasWarnings = (item.notificationWarnings ?? 0) > 0;

        if (
            notificationFilter.includes(NotificationStates.exception) &&
            notificationFilter.includes(NotificationStates.warning)
        ) {
            return hasExceptions && hasWarnings;
        }
        if (notificationFilter.includes(NotificationStates.exception)) {
            return hasExceptions;
        }
        if (notificationFilter.includes(NotificationStates.warning)) {
            return hasWarnings;
        }

        return false;
    });
};

export const getSortedItems = (
    rawData: CombinedAssetData[],
    transformedData: TransformedData[],
    sortBy: keyof CombinedAssetData,
    sortDir: SortDirectionType
): TransformedData[] => {
    // TODO: consider secondary sorting for:
    // - notifications (1. type, 2. amount (desc))
    // - activity (1. type, 2. duration (asc))

    // In case the sorting should be done on raw data, sort items first then
    // transform raw data so it can be rendered into the table. This means translating tables and formatting data.

    if (tableConfig.rawSortKeys.includes(sortBy)) {
        const sortedRawItems = sortItems(rawData, sortBy, sortDir);

        // Return transformed items in the same order as sorted raw items
        return compact(
            sortedRawItems.map(rawItem =>
                transformedData.find(transformedItem => rawItem.vehicleId === transformedItem.dataKey)
            )
        );
    }

    const sortKey = tableConfig.sortKeyReplacement?.[sortBy] ?? sortBy;
    return sortItems(transformedData, sortKey as keyof TransformedData, sortDir);
};

export const getFilteredTransformedData = createSelector(
    getData,
    getTransformedData,
    getActivityFilter,
    getAssetFilter,
    getNotificationFilter,
    getVisibleColumns,
    getSearchValue,
    getSortBy,
    getSortDir,
    getSelectedAssetGroupIds,
    (
        rawData,
        transformedData,
        activityFilter,
        assetFilter,
        notificationFilter,
        visibleColumns,
        searchValue,
        sortBy,
        sortDir,
        groupIds
    ) => {
        let result = filterByActivity(transformedData, activityFilter);
        result = filterByAssetTypesOrDriver(result, assetFilter);
        result = filterByNotifications(result, notificationFilter);
        result = searchItems(result, visibleColumns, searchValue);
        result = filterByGroups(result, groupIds);
        result = getSortedItems(rawData, result, sortBy, sortDir);
        return result;
    }
);
